<script setup lang="ts">
/**
 * The hero: a real tank turning, with a bearing dial reading off it.
 *
 * The dial is the page's signature and the reason the hero is 3-D at all. A
 * still photograph of a tank cannot say *the hull and the turret turn
 * independently* — it can only be captioned with it, and a caption is a claim.
 * A hull sweeping under a gun that is holding a bearing, with two numbers
 * disagreeing beneath it, is a demonstration.
 *
 * ## Why a dial and not a gunsight
 *
 * The obvious instrument to borrow is the arena's own gunsight — fixed reticle,
 * reload ring, the gun marker riding the sight line. It would be wrong here, and
 * the reason is the camera: a gunsight means *you are looking down the gun*, and
 * this camera is a showroom camera looking at the vehicle from outside. Drawing
 * a sight over it would be an instrument lying about where the viewer is
 * standing.
 *
 * So the borrowed instrument is `RadarScope`'s instead — north-up, needles
 * rather than a rotating card, which is the honest way to show two bearings from
 * outside. It is also the readout a player will meet again in the bottom-right
 * of the HUD, so the vocabulary carries over.
 *
 * Drawn as inline SVG rather than as a raster, for the reason the radar bezel,
 * the title badges and the tutorial's key caps are all drawn: this renders at
 * `devicePixelRatio`, and a ring with tick marks is precisely the subject a
 * fixed image loses. SVG rather than the radar's canvas because there are two
 * needles and twelve ticks here, not a dozen blips a frame — a `transform`
 * attribute per needle is cheaper than a redraw, and it scales with `clamp()`.
 *
 * ## Failure is a still image, and it is a good one
 *
 * `heroSupported` is asked *before* an engine is constructed, so a machine
 * without WebGL, or a deploy where the GLBs are missing, is never charged for
 * the attempt. What it gets instead is the game's own night-foundry painting
 * with the dial still on it in its opening pose — which is the same claim, made
 * statically. It is also what the *first* paint shows in every case, so there is
 * no empty rectangle while the engine chunk is in flight and nothing reflows when
 * the tank arrives. The dial sits outside all of it: fed by the scene when there
 * is one, holding the opening pose when there is not, so the page cannot lose its
 * signature to a graphics failure.
 *
 * ## Babylon is fetched, never bundled
 *
 * `hero-scene` is imported **dynamically**, with only its types taken
 * statically — the discipline `HangarView` follows for `hangar-scene`, and for a
 * sharper reason here. This is the first screen an anonymous visitor sees, and a
 * 3-D engine on the critical path would delay the headline and the Play button
 * for the sake of an ornament below them. The still image and the dial paint
 * from the main bundle; the tank arrives when it arrives.
 */
import { nextTick, onBeforeUnmount, onMounted, ref, shallowRef } from 'vue';

import { liveryFor } from '@shared/liveries';
import { handheld } from '@/state/device';
import { HERO_LIVERY_POOL, HERO_TANK } from './landing-content';
import type { Bearings, HeroScene } from './hero-scene';

/** The opening pose, in whole degrees, matching `HeroScene`'s own initial state. */
const bearings = ref<Bearings>({ hull: 52, gun: 0, slewing: false });

const canvas = ref<HTMLCanvasElement | null>(null);
const frame = ref<HTMLElement | null>(null);
const scene = shallowRef<HeroScene | null>(null);

/**
 * Three states, not two.
 *
 * `pending` is what the first paint shows: the still image, already composed and
 * already carrying the dial, so there is no empty rectangle and no layout shift
 * whichever way the check goes. It becomes `live` only once the meshes are
 * actually in the scene, which is what stops the visitor watching a grey
 * placeholder block resolve into a tank.
 */
const state = ref<'pending' | 'live' | 'still'>('pending');

let observer: IntersectionObserver | null = null;

onMounted(async () => {
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

  const module = await import('./hero-scene').catch(() => null);
  if (!module) {
    state.value = 'still';
    return;
  }

  if (!(await module.heroSupported(HERO_TANK.hullId, HERO_TANK.turretId))) {
    state.value = 'still';
    return;
  }

  const element = canvas.value;
  if (!element) {
    state.value = 'still';
    return;
  }

  // A fresh pick per mount, i.e. per page load — see `HERO_LIVERY_POOL` for
  // why the pool is exactly the three liveries nobody had to spend a diamond
  // on. `HERO_TANK.paintId` is left alone: it still names the stock coat for
  // whatever else on the page reads it, this is only what gets rendered.
  const paintId = HERO_LIVERY_POOL[Math.floor(Math.random() * HERO_LIVERY_POOL.length)];

  const hero = new module.HeroScene(element, {
    ...HERO_TANK,
    paintId,
    onBearings: (next) => {
      bearings.value = next;
    },
  });
  scene.value = hero;

  await hero.ready;
  // The visitor may have pressed Play while the GLBs were in flight.
  if (!scene.value) return;

  state.value = 'live';
  // The canvas is `display: none` until this flag flips, so its client box is
  // zero until Vue has flushed — and `Engine.resize()` reads that box. Starting
  // before the flush sized the drawing buffer to nothing and the browser scaled
  // the result up: a visibly soft tank, on the one render the page is selling.
  await nextTick();

  prefetchOtherLiveries(paintId);

  if (reduced) {
    hero.renderOnce();
    return;
  }

  hero.start();

  // A hero scrolled past keeps its context — rebuilding it would mean fetching
  // the models again if the visitor scrolls back — but does no work.
  if (frame.value && 'IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => hero.setVisible(entries.some((e) => e.isIntersecting)),
      { threshold: 0 },
    );
    observer.observe(frame.value);
  }
});

onBeforeUnmount(() => {
  observer?.disconnect();
  observer = null;
  scene.value?.dispose();
  scene.value = null;
});

/**
 * Warms the browser's own cache with the pool's other pattern textures, so a
 * reload that rolls a different one has it waiting rather than fetching it
 * cold. Deliberately after the shown tank's own GLBs and pattern have
 * finished (this runs once `state` is `live`) — those are the render the
 * visitor is looking at, and a background fetch competing with them for the
 * same connection would be paying for polish with the thing it is polishing.
 *
 * Skipped on a handheld and on a metered connection, the same guard
 * `App.vue`'s `warmArenaScene` uses for the same reason: this is a cache
 * warm-up nobody asked for, and pulling extra bytes over somebody's cellular
 * data for it is the opposite of the point.
 */
function prefetchOtherLiveries(shown: string): void {
  if (handheld()) return;
  const connection = (
    navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
  ).connection;
  if (connection?.saveData) return;
  if (connection?.effectiveType && /^(slow-)?2g$/.test(connection.effectiveType)) return;

  const warm = () => {
    for (const id of HERO_LIVERY_POOL) {
      if (id === shown) continue;
      const pattern = liveryFor(id).pattern;
      if (!pattern) continue;
      const img = new Image();
      img.decoding = 'async';
      img.src = pattern;
    }
  };
  const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => void })
    .requestIdleCallback;
  if (idle) idle(warm);
  else window.setTimeout(warm, 1200);
}

/** Three digits, as a bearing is always written. */
function bearing(deg: number): string {
  return String(deg).padStart(3, '0');
}

/**
 * A needle's rotation, in the dial's own frame.
 *
 * The SVG is drawn with the needle pointing up at 0, and screen-clockwise is
 * increasing bearing — the same convention as the radar and as every compass, so
 * the number under the dial and the needle above it cannot disagree about which
 * way round the world goes.
 */
function needle(deg: number): string {
  return `rotate(${deg} 50 50)`;
}
</script>

<template>
  <div ref="frame" class="hero-frame">
    <div class="hero-plate" aria-hidden="true"></div>

    <canvas
      v-show="state === 'live'"
      ref="canvas"
      class="hero-canvas"
      aria-hidden="true"
    ></canvas>

    <img
      v-if="state !== 'live'"
      class="hero-still"
      src="/assets/loading/2.webp"
      alt="Two tanks holding a lane between foundry buildings at night"
    />

    <figure class="dial" :class="{ slewing: bearings.slewing }">
      <svg viewBox="0 0 100 100" role="img" aria-hidden="true">
        <circle class="dial-ring" cx="50" cy="50" r="46" />
        <g class="dial-ticks">
          <line
            v-for="i in 12"
            :key="i"
            x1="50"
            :y1="i % 3 === 1 ? 6 : 8"
            x2="50"
            y2="13"
            :transform="needle((i - 1) * 30)"
          />
        </g>
        <g class="dial-hull" :transform="needle(bearings.hull)">
          <path d="M50 18 L57 56 L50 50 L43 56 Z" />
        </g>
        <g class="dial-gun" :transform="needle(bearings.gun)">
          <line x1="50" y1="50" x2="50" y2="16" />
          <path d="M50 10 L54 16 L50 22 L46 16 Z" />
        </g>
        <circle class="dial-hub" cx="50" cy="50" r="3.4" />
      </svg>
      <figcaption>
        <span><b>Hull</b>{{ bearing(bearings.hull) }}°</span>
        <span class="gun"><b>Gun</b>{{ bearing(bearings.gun) }}°</span>
      </figcaption>
    </figure>
  </div>
</template>
